import React, { useEffect, useMemo } from 'react'
import { Platform, View } from 'react-native'

import { gap } from '@ui/theme/values'

import { Deal, DealType, StrapiID } from '@ca/common/types'
import EmptyResult from '@ui/components/EmptyResult'
import { useDropdownPastDeals } from '@shell/Topbar/dropdown/useDropdownStage'
import Dropdown from '@ui/elements/Dropdown'
import Segmented from '@ui/elements/Segmented'
import { SegmentedOption } from '@ui/elements/Segmented/types'
import useDealsByTags from '@order/hooks/useDealsByTags'
import { useBrandFilter } from '@order/hooks/useBrandFilter'
import { SelectSchema } from 'features/config/ConfigConditionSchemas'
import { useIsNavigationReady } from 'hooks/useIsNavigationReady'
import { useTranslation } from 'react-i18next'
import cn from '@ui/utils/cn'
import DealList from './DealList'
import { PersistedParams, useCardFilter } from './useCardFilter'
import { useDealsVariants } from '@order/hooks/useDealVariants'
import { useBaseDealPackagePrice } from './useBaseDealPackagePrice'

const DEFAULT_ACTIVE_TAGS = ['active']
const isWeb = Platform.OS === 'web'

interface Props {
  config: SelectSchema<'tags'>
  active?: SelectSchema<'tags'>
  onSelect?: (deal: Deal) => void
  independent?: boolean
  types?: DealType[]
  activeId?: StrapiID
  more?: boolean
  initial?: PersistedParams
  hideBrand?: boolean
  /**
   * If set overrides the brand required tags
   */
  requiredTags?: string[]
  /**
   * Enables the hidden deals
   */
  hidden?: boolean
}

export default function DealsIntro({
  config,
  onSelect,
  independent,
  activeId,
  active,
  initial,
  types,
  more = true,
  hideBrand = false,
  requiredTags,
  hidden = false,
}: Props) {
  const { t } = useTranslation(['tags', 'types', 'deals-types'])

  const brandFilter = useBrandFilter({ types })

  const filter = useCardFilter({
    initial: initial
      ? {
          tag: initial.tag ?? config.initial,
          type: initial.type,
          past_deals: initial.past_deals === '1',
          tags_excluded: initial.tags_excluded,
          price: initial.price,
        }
      : {
          tag: config.initial,
          type: brandFilter.activeBrandOption?.id as DealType | undefined,
        },
  })

  const showPast = filter.pastDeals.value

  const dropdownPastDeals = useDropdownPastDeals(null)

  const activeTags = active?.tags ?? DEFAULT_ACTIVE_TAGS

  const requiredTagsGroups = [
    !showPast ? activeTags : undefined,
    requiredTags?.length ? requiredTags : brandFilter.brandRequiredTags,
  ].filter((g): g is string[] => Boolean(g?.length))

  const mergedExcluded = [
    ...(filter.tagsExcluded ?? []),
    ...(brandFilter.brandExcludedTags ?? []),
  ]

  const baseDealPrice = useBaseDealPackagePrice()

  const deals = useDealsByTags({
    hidden: hidden ?? false,
    filterCards: true,
    types: brandFilter.isTagMode
      ? undefined
      : filter.type
      ? [filter.type]
      : undefined,
    requiredTags: requiredTagsGroups.length ? requiredTagsGroups : undefined,
    tagsExcluded: mergedExcluded.length ? mergedExcluded : undefined,
    priorityTags: brandFilter.isTagMode ? brandFilter.priorityTags : undefined,
    price: filter.price,
    baseDealPrice,
  })

  // Filter by tag if set or show all
  const dealsList = filter.tag ? deals.byTags[filter.tag] || [] : deals.all

  const dealsToShow = useDealsVariants(dealsList)

  const isNavReady = useIsNavigationReady()

  const tagOptions: SegmentedOption<string>[] = useMemo(() => {
    if (!deals.byTags) return []

    // Show non empty tags
    const tags = config.tags.filter((t) => deals.byTags[t]?.length > 0)

    return tags.map((tag) => ({
      id: tag,
      label: t(`tags:${tag}`),
    }))
  }, [deals.byTags, config.tags, t])

  const activeTagOption = useMemo(() => {
    return (
      tagOptions.find((option) => option.id === filter.tag) || tagOptions[0]
    )
  }, [filter.tag, tagOptions])

  const changeTagOption = (option: SegmentedOption<string>) => {
    filter.setTag(option.id)
  }

  const brandOptions: SegmentedOption<DealType>[] = hideBrand
    ? []
    : brandFilter.brandOptions.map((o) => ({
        id: o.id,
        label: t(`deals-types:deal.${o.id}`),
      }))

  const activeBrandOption =
    brandOptions.find((o) => o.id === brandFilter.activeBrandOption?.id) ??
    brandOptions[0]

  const changeBrandOption = (option: SegmentedOption<DealType>) => {
    brandFilter.setBrand(option.id)
  }
  const { tag: filterTag, setTag, type } = filter

  useEffect(() => {
    if (!isNavReady) return
    if (type && dealsToShow.length === 0) {
      setTag(config.initial)
    }
  }, [type, isNavReady])

  useEffect(() => {
    if (!isNavReady || !tagOptions.length) return
    if (filterTag && !tagOptions.some((o) => o.id === filterTag)) {
      setTag(tagOptions[0].id)
    }
  }, [isNavReady, tagOptions, filterTag, setTag])

  return (
    <>
      <View className="w-full flex-row items-center justify-center gap-8 px-8">
        {independent && (
          <>
            <View className="flex flex-1 flex-row items-center justify-center gap-8">
              {!!tagOptions.length && (
                <View
                  className={cn(
                    'flex-1',
                    'flex flex-row items-center justify-start',
                    !brandOptions.length && 'justify-center',
                    isWeb && 'order-2 justify-start'
                  )}
                >
                  <Segmented
                    options={tagOptions}
                    value={activeTagOption}
                    onChange={changeTagOption}
                  />
                </View>
              )}
              {brandOptions.length > 1 && (
                <View
                  className={cn(
                    'flex flex-row items-center justify-start',
                    !tagOptions.length && 'justify-center',
                    isWeb && 'order-1 flex-1 justify-end'
                  )}
                >
                  <Segmented
                    options={brandOptions}
                    value={activeBrandOption}
                    onChange={changeBrandOption}
                  />
                </View>
              )}
            </View>
            {more && (
              <Dropdown
                button={{
                  iconStart: 'dots',
                  size: 'sm',
                  variant: 'outlined',
                }}
                popoverProps={{
                  offset: [0, 12],
                }}
                width={240}
                items={[dropdownPastDeals]}
              />
            )}
          </>
        )}
        {!independent && (
          <Segmented
            options={tagOptions}
            value={activeTagOption}
            onChange={changeTagOption}
          />
        )}
      </View>
      {dealsToShow.length > 0 && (
        <View
          className="flex w-full flex-row flex-wrap justify-center"
          style={{
            gap,
            paddingHorizontal: gap,
            paddingTop: gap,
            paddingBottom: gap,
          }}
        >
          <DealList
            onSelect={onSelect}
            deals={dealsToShow ?? []}
            activeId={activeId}
          />
        </View>
      )}
      {dealsToShow.length === 0 && (
        <View className="flex w-full p-8">
          <EmptyResult
            title={t('modals:deals.empty.title')}
            desc={t('modals:deals.empty.desc')}
          />
        </View>
      )}
    </>
  )
}
